import { StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider';
import { enUS, es, ptBR } from "date-fns/locale";
import moment from 'moment';
import { SnackbarProvider } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, HashRouter as Router, Switch } from 'react-router-dom';
import './App.css';
import AlertDialog from './components/AlertDialog';
import { ContextMenuProvider } from './components/ContextMenu/ContextMenuProvider';
import Loading from './components/Loading';
import OverlayLoading from './components/OverlayLoading/OverlayLoading';
import PrivateRoute from './components/PrivateRoute';
import { LanzacoinShopProvider } from './context/LanzacoinShopContext';
import ChangePasswordPage from './pages/ChangePassword/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPassword/ForgotPasswordPage';
import LoginPage from './pages/Login/LoginPage';
import RootApp from './pages/RootApp';
import System from './pages/System';
import Support from './pages/Support';
import { logoutAction, successLoginAction, useAuthentication } from './reducer/authentication';
import { useCbxTheme } from './reducer/theme';
import AuthService from './service/AuthService';
import Settings from './service/Settings';
import { ThemeBlue, ThemeBlueDark } from './themes/ThemeBlue';
import UserType from './enum/UserType';

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme { }
}

moment.defineLocale('pt-br', {
    months: 'Janeiro_Fevereiro_Março_Abril_Maio_Junho_Julho_Agosto_Setembro_Outubro_Novembro_Dezembro'.split('_'),
    monthsShort: 'Jan_Fev_Mar_Abr_Mai_Jun_Jul_Ago_Set_Out_Nov_Dez'.split('_'),
    weekdays: 'Domingo_Segunda-feira_Terça-feira_Quarta-feira_Quinta-feira_Sexta-feira_Sábado'.split('_'),
    weekdaysShort: 'Dom_Seg_Ter_Qua_Qui_Sex_Sáb'.split('_'),
    weekdaysMin: 'Dom_2ª_3ª_4ª_5ª_6ª_Sáb'.split('_'),
    longDateFormat: {
        LT: 'HH:mm',
        L: 'DD/MM/YYYY',
        LL: 'D [de] MMMM [de] YYYY',
        LLL: 'D [de] MMMM [de] YYYY [às] LT',
        LLLL: 'dddd, D [de] MMMM [de] YYYY [às] LT',
        LTS: 'HH:mm:ss'
    },
    relativeTime: {
        future: 'em %s',
        past: '%s atrás',
        s: 'segundos',
        m: 'um minuto',
        mm: '%d minutos',
        h: 'uma hora',
        hh: '%d horas',
        d: 'um dia',
        dd: '%d dias',
        M: 'um mês',
        MM: '%d meses',
        y: 'um ano',
        yy: '%d anos'
    },
    ordinal: (number: number) => `${number}º`
});

let lang = window.navigator.languages[0].toLowerCase();
if (moment.locales().indexOf(lang) >= 0) {
    moment.locale(lang);
}

var dateLocale = ptBR;
if (lang.startsWith('es')) {
    dateLocale = es;
} else if (lang.startsWith('en')) {
    dateLocale = enUS;
}

async function loadSettings() {
    try {
        const response = await fetch('Settings.json?q=' + Date.now());
        if (response.ok) {
            const settings = await response.json();
            Settings.set(settings);
        }
    } catch (e) {
        console.error('getSettings', e);
    }
}

function App() {

    const [loaded, setLoaded] = useState<boolean>(false);
    const [theme, setTheme] = useState<Theme>(ThemeBlue);
    const { user } = useAuthentication();
    const cbxTheme = useCbxTheme();
    const dispatch = useDispatch(); 
    const snackRef = useRef<SnackbarProvider>(null);

    useEffect(() => {
        init();
    }, []);

    useEffect(() => {
        if (user) {
            if (cbxTheme === 'dark') {
                setTheme(ThemeBlueDark);
            } else {
                setTheme(ThemeBlue);
            }
        } else {
            setTheme(ThemeBlue);
        }
    }, [user, cbxTheme]);

    async function init() {
        await loadSettings();
        await verifyAuth();
        setLoaded(true);
    }

    async function verifyAuth() {
        var user = await AuthService.instance.getAuthUser();
        if (user) {
            dispatch(successLoginAction(user));
        } else {
            await AuthService.instance.logout();
            dispatch(logoutAction());
        }
    }

    if (loaded === false) {
        return <Loading />;
    }

    return (
        <div className="App" style={{ backgroundColor: theme.palette.background.default }}>
            <div id='overlay_editng' style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 3, display: 'none' }}></div>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <SnackbarProvider maxSnack={3} ref={snackRef} autoHideDuration={2000} disableWindowBlurListener ClickAwayListenerProps={{
                        onClickAway: () => {                            
                            snackRef.current?.closeSnackbar();
                        }
                    }} >                        
                        <ContextMenuProvider>
                            <LanzacoinShopProvider>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                                <OverlayLoading />
                                <AlertDialog />
                                <Router>
                                    <Switch>
                                        <Route exact path="/" component={RootApp} />
                                        <Route exact path="/login" component={LoginPage} />
                                        <Route exact path="/setPassword" component={ChangePasswordPage} />
                                        <Route exact path="/changePassword" component={ChangePasswordPage} />
                                        <Route exact path="/forgotPassword" component={ForgotPasswordPage} />
                                        <PrivateRoute userType={[UserType.Commbox, UserType.Undefined]} path="/system" component={System} />
                                        <PrivateRoute userType={[UserType.FinalCustomer, UserType.Integrator]} path="/support" component={Support} />
                                        <Route render={() => <Redirect to="/" />} />
                                    </Switch>
                                </Router>
                            </LocalizationProvider>
                            </LanzacoinShopProvider>
                        </ContextMenuProvider>
                    </SnackbarProvider>
                </ThemeProvider>
            </StyledEngineProvider>
        </div>
    );
}

export default App;
